import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import DeleteConfirmModal from '../DeleteConfirmModal';
import DeleteIcon from './DeleteIcon';
import './ScanCardActions.scss';
import VerticalEllipsisIcon from './VerticalEllipsisIcon';

/**
 * ScanCardActions component - displays an ellipsis button that toggles
 * a popup menu with a delete action for a scan card.
 * When delete is clicked, shows an inline confirmation popover before calling onDeleteClick.
 * @param {Function} onDeleteClick - Callback when delete is confirmed
 * @param {Object} scan - The scan object
 * @returns React component that renders the actions menu
 */
const ScanCardActions = ({ onDeleteClick, scan }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const handleToggleMenu = useCallback(
    (e) => {
      e.stopPropagation();
      setMenuOpen((prev) => !prev);
    },
    []
  );

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      setMenuOpen(false);
      setConfirmOpen(true);
    },
    []
  );

  const handleConfirmDelete = useCallback(
    () => {
      setConfirmOpen(false);
      onDeleteClick?.(scan);
    },
    [onDeleteClick, scan]
  );

  const handleCancelDelete = useCallback(
    () => {
      setConfirmOpen(false);
    },
    []
  );

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className={`scan-card-actions ${menuOpen || confirmOpen ? 'menu-open' : ''}`}>
      <div
        className="scan-card-actions-trigger"
        ref={triggerRef}
        onClick={handleToggleMenu}
        data-testid="scan-card-actions-trigger"
      >
        <VerticalEllipsisIcon />
      </div>
      {menuOpen && (
        <div
          className="scan-card-actions-menu"
          ref={menuRef}
          data-testid="scan-card-actions-menu"
        >
          <button
            className="scan-card-actions-menu-item"
            onClick={handleDelete}
            data-testid="scan-card-action-delete"
            title="Delete"
          >
            <DeleteIcon />
          </button>
        </div>
      )}
      <DeleteConfirmModal
        open={confirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        scanName={scan?.name}
      />
    </div>
  );
};

ScanCardActions.propTypes = {
  onDeleteClick: PropTypes.func,
  scan: PropTypes.object
};

export default ScanCardActions;
